import { useCallback, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { t } from '../lib/strings.ts';

interface ConfirmOptions {
  title?: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use the danger button style for the confirm action (destructive actions). Defaults to true. */
  danger?: boolean;
}

interface PendingState extends Required<Pick<ConfirmOptions, 'title' | 'confirmLabel' | 'cancelLabel' | 'danger'>> {
  body?: string;
  resolve: (value: boolean) => void;
}

/**
 * useConfirm — replaces window.confirm() with an app-styled Radix dialog.
 *
 * Usage:
 *   const { confirm, dialog } = useConfirm();
 *   ...
 *   if (!(await confirm({ body: t.manageTypes.deleteConfirm }))) return;
 *   ...
 *   return <>{dialog}{...rest of component}</>;
 *
 * `confirm()` resolves `true` if the user confirmed, `false` if cancelled/dismissed.
 */
export function useConfirm(): {
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
  dialog: JSX.Element;
} {
  const [pending, setPending] = useState<PendingState | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options?: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending({
        title: options?.title ?? t.confirmDialog.defaultTitle,
        body: options?.body,
        confirmLabel: options?.confirmLabel ?? t.confirmDialog.confirm,
        cancelLabel: options?.cancelLabel ?? t.confirmDialog.cancel,
        danger: options?.danger ?? true,
        resolve,
      });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setPending(null);
  }, []);

  const dialog = (
    <Dialog.Root
      open={pending !== null}
      onOpenChange={(open) => { if (!open) settle(false); }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content className="card-paper fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 space-y-4 p-5 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95">
          <Dialog.Title className="font-serif text-lg text-foreground">
            {pending?.title}
          </Dialog.Title>
          {pending?.body && (
            <Dialog.Description className="text-sm text-muted-foreground">
              {pending.body}
            </Dialog.Description>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary btn-sm" onClick={() => settle(false)}>
              {pending?.cancelLabel}
            </button>
            <button
              type="button"
              className={pending?.danger ? 'btn-danger btn-sm' : 'btn-primary btn-sm'}
              onClick={() => settle(true)}
              autoFocus
            >
              {pending?.confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );

  return { confirm, dialog };
}
